import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { AvailabilityCard, type AvailabilityWindow } from '../components/AvailabilityCard.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { useToast } from '../components/Toaster.tsx';

interface AgentLite {
  id: string;
  email: string;
  name: string;
  role: 'agent' | 'admin' | 'time_admin';
  isActive: boolean;
  isFullTime: boolean;
  phoneNumber: string | null;
  calendarBookingUrl: string | null;
}

type SkillLevel = 'prime' | 'second' | 'learn';
interface SkillLite {
  id: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
}
interface AgentSkillCell {
  agentId: string;
  skillId: string;
  level: SkillLevel;
}

const LEVEL_LABEL: Record<SkillLevel, string> = {
  prime: 'Prime',
  second: 'Second',
  learn: 'Learn',
};

// Strip everything except digits so wa.me accepts whatever the agent
// entered ("+1 (555) 123-4567" → "15551234567"). wa.me expects no plus.
function whatsappUrl(phone: string): string {
  const digits = phone.replace(/\D+/g, '');
  return `https://wa.me/${digits}`;
}

// Public per-agent profile. Any signed-in agent can view this; admins
// and the agent themselves can also edit availability from here. Other
// editable fields (name, calendar URL) stay on My profile to avoid
// duplicating the edit surface.
export function AgentDetailPage(): React.ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const { agent: me } = useAuth();
  const toast = useToast();
  const [agent, setAgent] = useState<AgentLite | null>(null);
  const [availability, setAvailability] = useState<AvailabilityWindow[]>([]);
  const [skills, setSkills] = useState<SkillLite[]>([]);
  const [agentSkills, setAgentSkills] = useState<AgentSkillCell[]>([]);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle(agent?.name ?? 'Technician');

  useEffect(() => {
    apiFetch<{
      agent: AgentLite;
      availability: AvailabilityWindow[];
      skills: AgentSkillCell[];
    }>(`/api/agents/${id}`)
      .then((r) => {
        setAgent(r.agent);
        setAvailability(r.availability);
        setAgentSkills(r.skills);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load technician.');
      });
    apiFetch<{ skills: SkillLite[] }>('/api/skills')
      .then((r) => setSkills(r.skills))
      .catch(() => undefined);
  }, [id]);

  const skillsByLevel = useMemo(() => {
    const byId = new Map(skills.map((s) => [s.id, s]));
    const groups: Record<SkillLevel, SkillLite[]> = { prime: [], second: [], learn: [] };
    for (const cell of agentSkills) {
      const skill = byId.get(cell.skillId);
      if (skill) groups[cell.level].push(skill);
    }
    for (const k of Object.keys(groups) as SkillLevel[]) {
      groups[k].sort((a, b) => a.displayOrder - b.displayOrder);
    }
    return groups;
  }, [skills, agentSkills]);

  async function setSkillLevel(skillId: string, level: SkillLevel | null): Promise<void> {
    try {
      await apiFetch(`/api/agents/${id}/skills/${skillId}`, {
        method: 'PUT',
        json: { level },
      });
      setAgentSkills((cur) => {
        const without = cur.filter((c) => c.skillId !== skillId);
        return level ? [...without, { agentId: id, skillId, level }] : without;
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update skill.');
    }
  }

  if (error) {
    return (
      <Card>
        <p className="text-sm text-[var(--color-danger)]">{error}</p>
      </Card>
    );
  }
  if (!agent) {
    return <p className="text-[var(--color-muted)]">Loading…</p>;
  }

  const canEdit = me?.id === agent.id || me?.role === 'admin';

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link to="/dashboard" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Dashboard
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">{agent.name}</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          <span className="capitalize">{agent.role.replace('_', ' ')}</span>
          {!agent.isFullTime && (
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
              Part-time
            </span>
          )}
        </p>
      </div>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Contact</h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Email</dt>
            <dd>
              <a href={`mailto:${agent.email}`} className="text-[var(--color-t3-blue)] hover:underline">
                {agent.email}
              </a>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Phone</dt>
            <dd>
              {agent.phoneNumber ? (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`tel:${agent.phoneNumber}`}
                    className="text-[var(--color-t3-blue)] hover:underline"
                  >
                    {agent.phoneNumber}
                  </a>
                  <a
                    href={whatsappUrl(agent.phoneNumber)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-200"
                    title="Open WhatsApp chat"
                  >
                    WhatsApp →
                  </a>
                </div>
              ) : (
                <span className="text-[var(--color-muted)]">—</span>
              )}
            </dd>
          </div>
          {agent.calendarBookingUrl && (
            <div className="col-span-2">
              <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
                Personal booking link
              </dt>
              <dd>
                <a
                  href={agent.calendarBookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--color-t3-blue)] hover:underline"
                >
                  {agent.calendarBookingUrl}
                </a>
              </dd>
            </div>
          )}
        </dl>
      </Card>

      <AvailabilityCard
        agentId={agent.id}
        initial={availability}
        canEdit={canEdit}
        onSaved={(w) => setAvailability(w)}
      />

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-header)]">Skills</h2>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              {canEdit
                ? 'Click a skill to cycle Prime → Second → Learn → off.'
                : 'Set by the technician (or an admin).'}
            </p>
          </div>
          <Link
            to="/team"
            className="text-xs font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            View team matrix →
          </Link>
        </div>
        <div className="mt-3 space-y-3">
          {(['prime', 'second', 'learn'] as SkillLevel[]).map((lvl) => (
            <SkillGroup
              key={lvl}
              level={lvl}
              skills={skillsByLevel[lvl]}
            />
          ))}
          {canEdit && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                Add a skill
              </h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {skills
                  .filter((s) => !agentSkills.some((c) => c.skillId === s.id))
                  .map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => void setSkillLevel(s.id, 'prime')}
                      className="rounded-full border border-dashed border-[var(--color-border)] bg-white px-2 py-0.5 text-xs text-[var(--color-muted)] hover:border-[var(--color-t3-blue)] hover:text-[var(--color-t3-blue)]"
                      title="Add as Prime; click again in the row above to step down"
                    >
                      + {s.name}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  function SkillGroup({
    level,
    skills,
  }: {
    level: SkillLevel;
    skills: SkillLite[];
  }): React.ReactElement {
    if (skills.length === 0 && !canEdit) return <></>;
    const chipCls =
      level === 'prime'
        ? 'bg-[var(--color-t3-blue)] text-white'
        : level === 'second'
          ? 'bg-[var(--color-t3-purple)] text-white'
          : 'bg-amber-100 text-amber-900 border border-amber-300';
    return (
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
          {LEVEL_LABEL[level]}
        </h3>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {skills.length === 0 ? (
            <span className="text-xs italic text-[var(--color-muted)]">None</span>
          ) : (
            skills.map((s) => {
              const nextLevel: SkillLevel | null =
                level === 'prime' ? 'second' : level === 'second' ? 'learn' : null;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={canEdit ? () => void setSkillLevel(s.id, nextLevel) : undefined}
                  disabled={!canEdit}
                  className={
                    `rounded-full px-2 py-0.5 text-xs font-medium ${chipCls} ` +
                    (canEdit ? 'cursor-pointer hover:brightness-95' : 'cursor-default')
                  }
                  title={
                    canEdit
                      ? `Click to ${nextLevel ? `move to ${LEVEL_LABEL[nextLevel]}` : 'remove'}`
                      : s.name
                  }
                >
                  {s.name}
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  }
}
